/**
 * Import-success panel. Rendered after `rescueImportPetsWithChips`
 * returns with `validationFailed: false`. The import is all-or-nothing,
 * so every submitted row committed. Shows a hero + count, stat card,
 * and follow-up links (jump to chips page, download import log).
 */

import React from 'react';
import { useHistory } from 'react-router-dom';
import Button from '../../../components/Button/Button';
import { ModalActions } from '../../../components/Modal/Modal';
import type { RescueImportPetsWithChipsRowResult } from '../../../lib/integrationQueries';
import { CheckIcon, DownloadIcon, SearchIcon } from './icons';
import { displayRowNumber } from './validateRow';
import { csvField } from './csvWriter';
import styles from '../Integrations.module.scss';

interface ImportSuccessProps {
  successCount: number;
  resultRows: RescueImportPetsWithChipsRowResult[];
  fileName: string | null;
  onClose: () => void;
  onUploadAnother: () => void;
}

function downloadImportLog(resultRows: RescueImportPetsWithChipsRowResult[], fileName: string | null) {
  if (resultRows.length === 0) {
    return;
  }
  const header = 'row,microchip_id,pet_id';
  const lines = resultRows.map((r) =>
    // 1-based data row number — strip the header offset so the log
    // reads the same way the review table did at upload time.
    [csvField(displayRowNumber(r.rowIndex)), csvField(r.chipSerialNumber), csvField(r.petId)].join(','),
  );
  const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${fileName ? fileName.replace(/\.csv$/i, '') : 'pet-import'}-import-log.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer the revoke — some browsers (notably older Safari) start the
  // download asynchronously after `.click()`, and revoking immediately
  // can cancel an in-flight save.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export default function ImportSuccess({
  successCount,
  resultRows,
  fileName,
  onClose,
  onUploadAnother,
}: ImportSuccessProps) {
  const history = useHistory();

  return (
    <div className={styles.wizardStep}>
      <div className={styles.successWrap}>
        <div className={styles.successHero}>
          <CheckIcon />
        </div>
        <h3 className={styles.successTitle}>
          {successCount} chip{successCount === 1 ? '' : 's'} registered
        </h3>
        <p className={styles.successBody}>
          They&rsquo;re searchable in the Fi Nano network now, and a confirmation email is on its way to each registered
          owner.
        </p>

        <div className={styles.successStats}>
          <div className={styles.successStat}>
            <div className={styles.successStatValue}>{successCount}</div>
            <div className={styles.successStatLabel}>Published &middot; Emails sent</div>
          </div>
        </div>

        <div className={styles.successLinks}>
          <button
            type="button"
            className={styles.successLink}
            onClick={() => {
              history.push('/rescue/chips');
              onClose();
            }}
          >
            <SearchIcon />
            Go to all chips
          </button>
          <button type="button" className={styles.successLink} onClick={() => downloadImportLog(resultRows, fileName)}>
            <DownloadIcon />
            Download import log (CSV)
          </button>
        </div>
      </div>

      <ModalActions>
        <Button variant="ghost" onClick={onUploadAnother}>
          Upload another
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      </ModalActions>
    </div>
  );
}
